import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Divider, Tabs, Typography, styled, } from '@mui/material';
import { useTokens, } from '@mcn-platform/models';
import { StyledTab, TabPanel } from '../../common/CustomTab';
import Resources from '../../item/LandingPageTabs/resourceContent/Resources';
import { infoTabs } from '../../course/constants';
import DiscussionGroup from '../../discussionGroup/DiscussionGroup';
import ConversationWidget from '../../clientAi/ConversationWidget';
import Transcript from './Transcript';
const StyledDivider = styled(Divider)(() => ({
    borderBottomWidth: 2.5,
    marginTop: 0.4,
    marginBottom: 1,
}));
function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
        className: 'courseDeliveryTabs',
    };
}
const InfoTabs = ({ item, section, settings: { defaultInfoTab, showOverviewTab, showMaterialTab, showTranscriptTab, showQnATab, }, }) => {
    const [tabIndex, setTabIndex] = React.useState(0);
    const tokens = useTokens();
    const resources = item.filterSections('download');
    // its a course with a microcast as a lesson
    const transcript = section?.item?.microcast?.microcastAsset?.transcript
        // or its a course with another type of lesson
        || section?.item?.asset?.transcript
        // its a microcast
        || item?.microcastAsset?.transcript;
    const courseName = item.name.toUpperCase();
    const { discussionTopic } = item;
    useEffect(() => {
        setTabIndex(defaultInfoTab);
    }, [defaultInfoTab]);
    const handleChange = (event, newValue) => {
        setTabIndex(newValue);
    };
    return (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Box, { children: _jsxs(Tabs, { value: tabIndex, onChange: handleChange, "aria-label": "Course Resources", variant: "fullWidth", TabIndicatorProps: {
                        style: {
                            display: 'none',
                        },
                    }, children: [showOverviewTab && (_jsx(StyledTab, { value: 0, sx: { mr: 0.625 }, label: tokens.getToken('courseOverviewLabel'), ...a11yProps(0) })), showMaterialTab && resources.length > 0 && (_jsx(StyledTab, { value: 1, sx: { mr: 0.625 }, label: tokens.getToken('courseMaterialsLabel'), ...a11yProps(1) })), showTranscriptTab && transcript?.length > 0 && (_jsx(StyledTab, { value: 2, label: tokens.getToken('transcriptLabel'), ...a11yProps(2) })), showQnATab && (_jsx(StyledTab, { value: 3, label: tokens.getToken('qnaLabel'), ...a11yProps(2) })), discussionTopic && (_jsx(StyledTab, { value: 4, label: "Discussion", ...a11yProps(3) }))] }) }), showOverviewTab && (_jsxs(TabPanel, { isSelected: tabIndex === 0, index: 0, firstTab: true, children: [_jsx(Typography, { variant: "subtitle1", fontWeight: "fontWeightBold", children: courseName }), _jsx(StyledDivider, {}), _jsx(Typography, { variant: "body1", sx: { pt: 1, whiteSpace: 'pre-line' }, children: item.description })] })), showMaterialTab && (_jsxs(TabPanel, { isSelected: tabIndex === 1, index: 1, children: [_jsx(Typography, { variant: "subtitle1", fontWeight: "fontWeightBold", children: courseName }), _jsx(StyledDivider, {}), _jsx(Box, { sx: { pt: 1 }, children: _jsx(Resources, { items: resources, settings: { showTitle: false, showResources: true } }) })] })), showTranscriptTab && transcript && (_jsx(TabPanel, { isSelected: tabIndex === 2, index: 2, children: _jsx(Transcript, { transcript: transcript, courseName: courseName }) })), showQnATab && (_jsx(TabPanel, { isSelected: tabIndex === 3, index: 3, children: _jsx(ConversationWidget, { limitToPageWidth: false, showItemReferencesWithMessage: true, showItemReferencesCarousel: false, courseCardShowImage: true, courseCardShowTitle: true, courseCardShowInstructor: false, courseCardShowDescLines: 3, openWebPagesInNewTab: false, hideRightRailOnMobile: false, maxItemsInRightRail: 8, style: {
                        backgroundColor: 'rgba(0,0,0,0)',
                        borderColor: '#ccc',
                        borderRadius: 4,
                        borderWidth: 1,
                        color: 'color_font',
                        margin: 4,
                        padding: 8,
                        shadow: 0,
                    }, questionStyle: {
                        backgroundColor: '#f9f9f9',
                        borderColor: '#ddd',
                        borderRadius: 8,
                        borderWidth: 1,
                        color: '#000',
                        margin: 0,
                        padding: 12,
                    }, responseStyle: {
                        backgroundColor: '#e9e9e9',
                        borderColor: '#bbb',
                        borderRadius: 8,
                        borderWidth: 1,
                        color: '#000',
                        margin: 0,
                        padding: 12,
                    }, inputStyle: {
                        backgroundColor: '#fff',
                        borderColor: '#999',
                        borderRadius: 4,
                        borderWidth: 1,
                        color: '#000',
                        margin: 4,
                        padding: 8,
                    }, startingMessageFontStyle: {
                        fontSize: 16,
                        fontWeight: '600',
                        color: '#000',
                    }, adminOptions: {
                        systemPromptOverride: item?.systemPrompt || 'Welcome to the course!',
                        returnSystemPrompt: true,
                    } }) })), discussionTopic && (_jsx(TabPanel, { isSelected: tabIndex === 4, index: 4, lastTab: true, children: _jsx(DiscussionGroup, { topic: discussionTopic }) }))] }));
};
InfoTabs.defaultProps = {
    section: undefined,
    settings: {
        defaultInfoTab: 0,
        showOverviewTab: true,
        showMaterialTab: true,
        showTranscriptTab: true,
        showQnATab: true,
    },
};
InfoTabs.meta = {
    group: 'hidden',
    name: 'Course Lesson Panel',
    description: 'Component displays the course lesson details and player.',
    props: [
        {
            key: 'course',
            name: 'Course',
            type: 'course', // note this is a special type recognized by the system
            description: 'The course to display information about',
        },
        {
            key: 'courseItem',
            name: 'Course Item',
            type: 'courseItem', // note this is a special type recognized by the system
            description: 'The course item to display information about',
        },
        {
            key: 'settings.defaultInfoTab',
            name: 'Default video tab',
            type: 'array',
            items: infoTabs,
            description: 'Default course video tab like overview or material or transcript.',
        },
        {
            key: 'settings.showOverviewTab',
            name: 'Show overview tab',
            type: 'boolean',
            description: 'Show course overiew tab.',
        },
        {
            key: 'settings.showMaterialTab',
            name: 'Show material tab',
            type: 'boolean',
            description: 'Show course material tab.',
        },
        {
            key: 'settings.showTranscriptTab',
            name: 'Show transcript tab',
            type: 'boolean',
            description: 'Show course transcript tab.',
        },
        {
            key: 'settings.showQnATab',
            name: 'Show Q&A tab',
            type: 'boolean',
            description: 'Enable the Q&A tab to provide an AI-powered question-and-answer feature.',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'InfoTabs',
        props: {
            course: '@course',
            courseItem: '@courseItem',
            settings: {
                defaultInfoTab: 0,
                showOverviewTab: true,
                showMaterialTab: true,
                showTranscriptTab: true,
                showQnATab: true,
            },
        },
    },
};
export default observer(InfoTabs);
//# sourceMappingURL=InfoTabs.js.map